import { yupResolver } from '@hookform/resolvers/yup'
import { Scan } from 'lucide-react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { Controller, useForm } from 'react-hook-form'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { PageLayout } from '@/components'
import { Card, CardContent } from '@/components/ui'
import { Button } from '@/components/ui/button'
import ShowCaptcha from '../../../core/styles/showCaptcha'
import { getTranslation, requestForgotPasswordCode } from '../../services/auth'
import { FeatureCarousel } from '../FeatureCarousel'
import {
  PhoneNumberCountry,
  CountryAndPhone,
} from '@/components/phone-number-country'
import translate from '@/core/services/translate'
import { CountryData } from 'country-codes-list'

interface AuthPhoneValues {
  phoneData: CountryAndPhone
}

export const ForgotPasswordPage: React.FC = () => {
  const navigation = useNavigate()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const language = getTranslation()

  const schema = yup.object().shape({
    phoneData: yup.object().shape({
      countryCode: yup.string().required('Country is required'),
      countryCodeValue: yup.string().required(),
      phoneNumber: yup
        .string()
        .required('Phone number is required')
        .when('countryCallingCode', (countryCallingCode) => {
          if (countryCallingCode) {
            return yup
              .string()
              .test(
                'Valid phone number',
                translate(language.value, 'common.validPhoneNumber'),
                (value, context) => {
                  const country: CountryData = context.parent.countryCodeValue
                  if (value) return isValidPhoneNumber(`${country}${value}`)
                  return false
                },
              )
          } else {
            return yup.string().required('Please Select Country!')
          }
        })
        .required(),
    }),
  })

  const getCaptchaToken = async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return ''
    }

    return await executeRecaptcha('requestCode')
  }

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<AuthPhoneValues>({
    mode: 'onChange',
    resolver: yupResolver<AuthPhoneValues>(schema),
    defaultValues: {
      phoneData: {
        countryCode: 'RO',
        countryCodeValue: '40',
        phoneNumber: '',
      },
    },
  })

  const submit = async (data: AuthPhoneValues) => {
    const fullPhone = `${data.phoneData.countryCodeValue}${data.phoneData.phoneNumber}`
    try {
      await requestForgotPasswordCode(fullPhone, await getCaptchaToken())

      // Store phone in sessionStorage as backup
      sessionStorage.setItem('reset_password_phone', fullPhone)

      navigation('/account/reset-password', {
        state: { phone: fullPhone },
      })
    } catch (error) {
      console.error('Error requesting code:', error)
    }
  }

  const features = [
    {
      icon: (
        <div className="w-16 h-16 rounded-2xl bg-green-400/20 flex items-center justify-center">
          <Scan className="w-8 h-8 text-green-400" />
        </div>
      ),
      title: 'Fast & Secure Check-In',
      description:
        'Say goodbye to paper tickets and manual lists. Our blockchain-powered check-in system allows for instant ticket validation—whether scanned at the door or verified on-chain.',
    },
  ]

  return (
    <>
      <ShowCaptcha />
      <PageLayout authPage>
        <div className="container flex mt-12">
          {/* Left Side - Features Section */}
          <div className="relative w-1/2 flex flex-col">
            <div className="flex-1 flex items-center justify-center">
              <FeatureCarousel features={features} />
            </div>
          </div>

          {/* Right Side - Form */}
          <Card className="w-[527px]">
            <CardContent className="flex flex-col gap-5 items-center w-full px-24 py-16">
              <div className="w-full">
                <div className="text-title-m text-center mb-2">
                  Forgot Password
                </div>
                <div className="text-body-medium text-monochrome-2 text-center mb-8">
                  Please enter your details.
                </div>

                <form onSubmit={handleSubmit(submit)} className="space-y-7">
                  <Controller
                    name="phoneData"
                    control={control}
                    render={({ field }) => (
                      <PhoneNumberCountry
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={
                          errors.phoneData?.countryCode?.message ||
                          errors.phoneData?.phoneNumber?.message
                        }
                      />
                    )}
                  />

                  <Button className="w-full" type="submit">
                    Continue
                  </Button>
                </form>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
      <div className="flex justify-center mt-5">
        <div id="recaptcha-custom"></div>
      </div>
    </>
  )
}

export default ForgotPasswordPage
